import React, { ReactNode, useEffect } from 'react';
import dynamic from 'next/dynamic';
import ReactModal from 'react-modal';
import { useRouter } from 'next/router';
import { TrackProvider } from '@components/analytics/context/AnalyticsContext';
import AlertBanner from '@/components/organisms/AlertBanner';
import ErrorBoundary from '@/components/organisms/ErrorBoundary/ErrorBoundary';
import { AppState } from '@/components/context/Silverstripe';
import MetaLayout from './MetaLayout';
import ThemeLayout from './ThemeLayout';

ReactModal.setAppElement('#__next');

// Import MainMenu dynamically since it's not on every page (showMenu variable) e.g. it's not on home page (has its own menu)
const MainMenu = dynamic(() => import('@/components/organisms/Navigation/Menu'));

export interface SiteLayoutProps {
  children: ReactNode;
  appState?: AppState;
  preview?: boolean;
  showMenu?: boolean;
  showFooter?: boolean;
  footerComponent?: ReactNode;
  menuOnlyShowLogo?: boolean;
}

const SiteLayout = ({
  children,
  appState,
  preview = false,
  showMenu = true,
  showFooter = true,
  footerComponent = null,
  menuOnlyShowLogo = false,
}: SiteLayoutProps) => {
  const router = useRouter();
  const pathName = router.pathname;

  // handle attaching event handlers for css regions modal
  useEffect(() => {
    const selector = document.getElementsByClassName('modal-checkbox');
    const hideShowCSSModal = (evt) => {
      if (evt.target.checked) {
        document.body.classList.add('modal-open');
      } else {
        document.body.classList.remove('modal-open');
      }
    };
    const handleEscModalClose = (evt) => {
      if (evt.code === 'Escape' && document.body.classList.contains('modal-open')) {
        // escape
        Array.from(selector).forEach((element: HTMLInputElement) => {
          element.checked = false;
        });
        document.body.classList.remove('modal-open');
      }
    };
    if (selector) {
      Array.from(selector).forEach((element: HTMLInputElement) => {
        element.addEventListener('change', hideShowCSSModal);
      });
      document.addEventListener('keydown', handleEscModalClose);
    }
    const cssModalClose = () => {
      if (document.body.classList.contains('modal-open')) {
        // this is for the regions modal that is outside of the modal context.
        // Sometimes if the route has changed, the region modal may still be open e.g. the same route. So we need to close
        const modalCheckboxes = document.getElementsByClassName('modal-checkbox');
        if (modalCheckboxes) {
          Array.from(modalCheckboxes).forEach((element: HTMLInputElement) => {
            element.checked = false;
          });
        }
        if (document.body.classList.contains('modal-open')) {
          document.body.classList.remove('modal-open');
        }
      }
    };

    // set up event to close any hanging 'modal-open' css classes from modals
    router.events.on('routeChangeComplete', cssModalClose);
    router.events.off('routeChangeError', cssModalClose);

    return function checkboxModalCleanUp() {
      if (selector) {
        router.events.off('routeChangeComplete', cssModalClose);
        router.events.off('routeChangeError', cssModalClose);

        Array.from(selector).forEach((element: HTMLInputElement) => {
          element.removeEventListener('change', hideShowCSSModal);
        });
        document.removeEventListener('keydown', handleEscModalClose);
      }
    };
  }, []);

  return (
    <ErrorBoundary>
      <MetaLayout appState={appState}>
        <ThemeLayout footerComponent={footerComponent} footer={showFooter}>
          <TrackProvider component_name="alert banner">
            {!pathName.startsWith('/booking') && (
              <AlertBanner preview={preview} siteConfig={appState?.global?.siteConfig} />
            )}
          </TrackProvider>
          {showMenu && <MainMenu onlyLogo={menuOnlyShowLogo} />}
          <ErrorBoundary>{children}</ErrorBoundary>
        </ThemeLayout>
      </MetaLayout>
    </ErrorBoundary>
  );
};

export default SiteLayout;
